import type { UIMessage } from 'ai';

import { getDataSuggestionsOptions, getFollowUpsAfterLastUserMessage } from './data-suggestions';
import { HUMAN_AGENT_MESSAGE_ROLE, humanAgentUiMessage } from './human-agent-message';

export function advisorMessageFeedbackRating(message: UIMessage): 'up' | 'down' | null {
  const raw = message.metadata;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const v = (raw as { feedbackRating?: unknown }).feedbackRating;
  return v === 'up' || v === 'down' ? v : null;
}

export function advisorMessageFeedbackComment(message: UIMessage): string | null {
  const raw = message.metadata;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const v = (raw as { feedbackComment?: unknown }).feedbackComment;
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

/** Shallow-merge metadata for optimistic feedback updates after ``submitFeedback``. */
export function advisorUiMessageWithMetadataPatch(message: UIMessage, patch: Record<string, unknown>): UIMessage {
  const prev =
    message.metadata && typeof message.metadata === 'object' && !Array.isArray(message.metadata)
      ? { ...(message.metadata as Record<string, unknown>) }
      : {};
  return { ...message, metadata: { ...prev, ...patch } };
}

const HTTP_ERROR_BODY_MAX = 500;

/** Bound size of non-JSON error bodies (e.g. HTML proxy pages) before wrapping in ``Error``. */
export function advisorV2HttpErrorSnippet(raw: string, max = HTTP_ERROR_BODY_MAX): string {
  const t = raw.trim();
  if (t.length <= max) {
    return t;
  }
  return `${t.slice(0, max)}…`;
}

/** UI-only message parts that are not stored in Postgres but must survive report-delivery poll refresh. */
const EPHEMERAL_UI_PART_TYPES = new Set([
  'data-suggestions',
  'data-kin-support-handoff',
  // Carries the live ticket chip (PS-1358); dropping it would blank the chip on a poll refresh.
  'data-kin-support-session',
]);

function ephemeralUiPartsFromMessage(message: UIMessage): UIMessage['parts'] {
  const parts = message.parts;
  if (!parts?.length) {
    return [];
  }
  return parts.filter(p => EPHEMERAL_UI_PART_TYPES.has(p.type ?? ''));
}

/** Assistant row that should receive carried follow-ups (chip-only shells map to the prior answer). */
function carryTargetAssistantId(current: readonly UIMessage[]): string | undefined {
  let lastUserIndex = -1;
  for (let i = current.length - 1; i >= 0; i -= 1) {
    if (current[i]?.role === 'user') {
      lastUserIndex = i;
      break;
    }
  }
  for (let i = current.length - 1; i > lastUserIndex; i -= 1) {
    const message = current[i];
    if (message?.role !== 'assistant') {
      continue;
    }
    if (!getDataSuggestionsOptions(message)?.length) {
      continue;
    }
    const hasDisplayText = (message.parts ?? []).some(
      part => part.type === 'text' && typeof part.text === 'string' && part.text.trim().length > 0
    );
    if (!hasDisplayText) {
      for (let j = i - 1; j > lastUserIndex; j -= 1) {
        if (current[j]?.role === 'assistant') {
          return current[j].id;
        }
      }
    }
    return message.id;
  }
  return undefined;
}

/**
 * Apply server thread rows while keeping streamed follow-up chips / handoff parts on matching ids.
 * ``useAdvisorPendingReportDeliveryPoll`` refetches after each stream; persisted rows are text-only.
 */
export function mergeAdvisorV2HistoryRowsIntoUiMessages(
  current: readonly UIMessage[],
  rows: readonly {
    role: string;
    content: string;
    message_id?: string | null;
    feedback_rating?: 'up' | 'down' | null;
    feedback_comment?: string | null;
    author_display_name?: string | null;
  }[],
  options?: { threadId?: string }
): UIMessage[] {
  const fromServer = advisorV2HistoryRowsToUiMessages(rows, options);
  if (!current.length) {
    return fromServer;
  }
  const currentById = new Map(current.map(message => [message.id, message]));
  let merged = fromServer.map(serverMessage => {
    const previous = currentById.get(serverMessage.id);
    if (!previous) {
      return serverMessage;
    }
    const ephemeral = ephemeralUiPartsFromMessage(previous);
    if (!ephemeral.length) {
      return serverMessage;
    }
    const serverTypes = new Set((serverMessage.parts ?? []).map(part => part.type));
    const toAppend = ephemeral.filter(part => !serverTypes.has(part.type));
    if (!toAppend.length) {
      return serverMessage;
    }
    return {
      ...serverMessage,
      parts: [...(serverMessage.parts ?? []), ...toAppend],
    };
  });

  const carriedFollowUps = getFollowUpsAfterLastUserMessage(current);
  const activeTurnAlreadyHasFollowUps = Boolean(getFollowUpsAfterLastUserMessage(merged)?.length);
  if (carriedFollowUps?.length && !activeTurnAlreadyHasFollowUps) {
    const targetId = carryTargetAssistantId(current);
    if (targetId) {
      merged = merged.map(message => {
        if (message.id !== targetId || getDataSuggestionsOptions(message)?.length) {
          return message;
        }
        return {
          ...message,
          parts: [
            ...(message.parts ?? []),
            { type: 'data-suggestions', data: { options: carriedFollowUps } },
          ],
        };
      });
    }
  }

  return merged;
}

/** Map agent ``GET /v2/chat/threads/{id}/messages`` rows to AI SDK UI messages. */
export function advisorV2HistoryRowsToUiMessages(
  rows: readonly {
    role: string;
    content: string;
    message_id?: string | null;
    feedback_rating?: 'up' | 'down' | null;
    feedback_comment?: string | null;
    author_display_name?: string | null;
  }[],
  options?: { threadId?: string }
): UIMessage[] {
  const idPrefix = options?.threadId ? `v2:${options.threadId}` : 'v2';
  const out: UIMessage[] = [];
  let index = 0;
  for (const row of rows) {
    if (row.role !== 'user' && row.role !== 'assistant' && row.role !== HUMAN_AGENT_MESSAGE_ROLE) {
      continue;
    }
    const stableId = row.message_id?.trim()
      ? row.message_id.trim()
      : `${idPrefix}:${index}:${row.role}`;
    if (row.role === HUMAN_AGENT_MESSAGE_ROLE) {
      out.push(humanAgentUiMessage(row, stableId));
      index += 1;
      continue;
    }
    const fr = row.feedback_rating;
    const fc =
      typeof row.feedback_comment === 'string' && row.feedback_comment.trim()
        ? row.feedback_comment.trim()
        : null;
    const metadata =
      row.role === 'assistant' && (fr === 'up' || fr === 'down')
        ? {
            feedbackRating: fr,
            ...(fr === 'down' && fc ? { feedbackComment: fc } : {}),
          }
        : undefined;
    out.push({
      id: stableId,
      role: row.role,
      parts: [{ type: 'text', text: row.content }],
      ...(metadata ? { metadata } : {}),
    });
    index += 1;
  }
  return out;
}
